import React from "react";

/**
 * Top-level Error Boundary — catches any React render/lifecycle exception in
 * the child tree and shows a friendly recovery screen instead of a blank page.
 * Without this, a single thrown error inside e.g. a toast/JSX child bubbles
 * up and unmounts the whole app root → white screen.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console so devs can still debug in production
    // eslint-disable-next-line no-console
    console.error("Unhandled UI error:", error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    // Full reload as last resort
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = "Ekran şu anda görüntülenemiyor. Lütfen yeniden deneyin; sorun sürerse yöneticinizle iletişime geçin.";
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-zinc-50 p-6"
        data-testid="global-error-boundary"
      >
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-lg shadow-sm p-6 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-4">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-zinc-900" data-testid="global-error-title">İşlem tamamlanamadı</h1>
          <p className="text-sm text-zinc-600 mt-1 break-words" data-testid="global-error-message">{msg}</p>
          <div className="mt-5 flex items-center justify-center gap-2">
            <button
              onClick={this.handleReset}
              className="px-3 py-2 text-sm font-semibold rounded-md bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="error-boundary-retry"
            >
              Tekrar Dene
            </button>
            <button
              onClick={this.handleReload}
              className="px-3 py-2 text-sm font-semibold rounded-md border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
              data-testid="error-boundary-reload"
            >
              Sayfayı Yenile
            </button>
          </div>
        </div>
      </div>
    );
  }
}
